import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Nav, Modal } from 'react-bootstrap';
import RequirementsList from '../Requirements/RequirementsList';
import RequirementForm from '../Forms/RequirementForm';
import RequirementProjection from '../Requirements/RequirementProjection';
import RequirementsTimelineTab from '../Requirements/RequirementsTimelineTab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faChartLine, faTimeline } from '@fortawesome/free-solid-svg-icons';
import '../../css/Bills.css';
import '../../css/Requirements.css';
import { getRequirements, updateRequirementOrder } from '../../services/RequirementService';
import DateService, { calculateReadableFortnight } from '../../services/DateService';
import { fortnightEndFromStart } from '../../utils/requirementCalculations';
import useRequirementsMobileLayout from '../../hooks/useRequirementsMobileLayout';
import RequirementsBottomNav from '../Requirements/RequirementsBottomNav';

const RequirementsPage = () => {
  const [activeTab, setActiveTab] = useState('#requirements');
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [requirements, setRequirements] = useState([]);
  const [fortnightIndex, setFortnightIndex] = useState(0);
  const [fortnightDatesIndex, setFortnightDatesIndex] = useState(null);
  const [formattedRangeFortnight, setFormattedRangeFortnight] = useState('');
  const [fortnightStart, setFortnightStart] = useState(null);
  const [fortnightEnd, setFortnightEnd] = useState(null);
  const [anchorFortnightStart, setAnchorFortnightStart] = useState(null);
  const abortControllerRef = useRef(null);
  const isMobileLayout = useRequirementsMobileLayout();
  const [bankSheetOpen, setBankSheetOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    DateService.getFortnightStartDate()
      .then((data) => {
        if (cancelled || !data?.fortnights?.[0]?.start_date) return;
        setAnchorFortnightStart(new Date(data.fortnights[0].start_date));
      })
      .catch((err) => console.error('Error loading fortnight anchor:', err.name));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadFortnight = async () => {
      try {
        const [startDate, formatted] = await calculateReadableFortnight(fortnightIndex);
        if (cancelled) return;
        setFortnightStart(startDate);
        setFortnightEnd(fortnightEndFromStart(startDate));
        setFormattedRangeFortnight(formatted);
        setFortnightDatesIndex(fortnightIndex);
      } catch (err) {
        console.error('Error loading fortnight range:', err.name);
      }
    };
    loadFortnight();
    return () => {
      cancelled = true;
    };
  }, [fortnightIndex]);

  useEffect(() => {
    fetchRequirements();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [refreshTrigger]);

  const fetchRequirements = async () => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      const data = await getRequirements(abortControllerRef.current.signal);
      setRequirements(data);
    } catch (error) {
      if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
        console.log('Requirements fetch was cancelled');
        return;
      }

      console.error('Error fetching requirements:', error.name || 'Unknown error');
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleEditRequirement = (requirement) => {
    setSelectedRequirement(requirement);
    setShowAddModal(true);
  };

  const handleAddSuccess = () => {
    setShowAddModal(false);
    setSelectedRequirement(null);
    handleRefresh();
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setSelectedRequirement(null);
  };

  const handleUpdateOrder = async (newRequirements) => {
    try {
      if (!Array.isArray(newRequirements)) {
        throw new Error('Requirements must be an array');
      }

      await updateRequirementOrder(newRequirements);
      setRequirements(newRequirements);
    } catch (error) {
      console.error('Error updating requirement order:', error.name);
    }
  };

  const openAddModal = () => {
    setSelectedRequirement(null);
    setShowAddModal(true);
  };

  return (
    <div className={`page-content requirements-page${isMobileLayout ? ' requirements-page--mobile' : ''}`}>
      <Card className="requirements-page-card">
        {!isMobileLayout && (
          <Card.Header className="bills-nav-header requirements-page-nav">
            <div className="requirements-page-nav-inner">
              <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab} className="requirements-page-tabs">
                <Nav.Item>
                  <Nav.Link eventKey="#requirements">
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-1 requirements-page-tab-icon" />
                    <span className="requirements-page-tab-label">Requirements</span>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="#timeline">
                    <FontAwesomeIcon icon={faTimeline} className="me-1 requirements-page-tab-icon" />
                    <span className="requirements-page-tab-label">Timeline</span>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="#projection">
                    <FontAwesomeIcon icon={faChartLine} className="me-1 requirements-page-tab-icon" />
                    <span className="requirements-page-tab-label">Budget</span>
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              <Button size="sm" className="bill-add-btn requirements-page-add-desktop" onClick={openAddModal}>
                + Requirement
              </Button>
            </div>
          </Card.Header>
        )}
        <Card.Body className="requirements-page-body">
          {activeTab === '#requirements' && (
            <RequirementsList
              requirements={requirements}
              fortnightIndex={fortnightIndex}
              fortnightDatesIndex={fortnightDatesIndex}
              setFortnightIndex={setFortnightIndex}
              formattedRangeFortnight={formattedRangeFortnight}
              fortnightStart={fortnightStart}
              fortnightEnd={fortnightEnd}
              onEdit={handleEditRequirement}
              onDelete={() => {}}
              onGenerateBills={() => {}}
              onGenerateAllDue={() => {}}
              onUpdateOrder={handleUpdateOrder}
              defaultViewMode="bank_account"
              onBankSheetOpenChange={setBankSheetOpen}
            />
          )}

          {activeTab === '#timeline' && (
            <RequirementsTimelineTab
              requirements={requirements}
              fortnightIndex={fortnightIndex}
              setFortnightIndex={setFortnightIndex}
              formattedRangeFortnight={formattedRangeFortnight}
              anchorFortnightStart={anchorFortnightStart}
              fortnightStart={fortnightStart}
              fortnightEnd={fortnightEnd}
            />
          )}

          {activeTab === '#projection' && (
            <RequirementProjection refreshTrigger={refreshTrigger} />
          )}
        </Card.Body>
      </Card>

      {isMobileLayout && !bankSheetOpen && (
        <RequirementsBottomNav activeTab={activeTab} onSelect={setActiveTab} />
      )}

      {activeTab === '#requirements' && isMobileLayout && (
        <button
          type="button"
          className="requirements-fab"
          onClick={openAddModal}
          aria-label="Add requirement"
          title="Add requirement"
        >
          +
        </button>
      )}

      <Modal
        show={showAddModal}
        onHide={handleCloseModal}
        size="lg"
        onClick={(e) => e.stopPropagation()}
      >
        <Modal.Header closeButton onClick={(e) => e.stopPropagation()}>
          <Modal.Title>
            {selectedRequirement
              ? `Edit Requirement: ${selectedRequirement.name}${
                  selectedRequirement.is_account_base ? ' — Base Allocation (min)' : ''
                }`
              : 'Add New Requirement'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body onClick={(e) => e.stopPropagation()}>
          <RequirementForm
            key={selectedRequirement?.id ?? 'new'}
            requirement={selectedRequirement}
            viewedFortnightStart={fortnightStart}
            viewedFortnightEnd={fortnightEnd}
            onSaved={handleAddSuccess}
            onCancel={handleCloseModal}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default RequirementsPage;
